import type { Editor } from '@tiptap/core';

export type PasteMode = 'original' | 'text' | 'no-colors' | 'no-background' | 'no-foreground';
export interface ClipboardContent { html: string; text: string; images: File[]; hasText: boolean }

function htmlText(html: string): string {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    doc.querySelectorAll('script,style,template,svg').forEach(element => element.remove());
    doc.querySelectorAll('br').forEach(element => element.replaceWith('\n'));
    doc.querySelectorAll('td,th').forEach(element => element.append('\t'));
    doc.querySelectorAll('p,div,li,h1,h2,h3,h4,h5,h6,tr,blockquote,pre').forEach(element => element.append('\n'));
    return (doc.body.textContent ?? '').replace(/\t\n/g, '\n').replace(/\n+$/, '');
}

export async function readClipboardContent(): Promise<ClipboardContent> {
    const clipboard = navigator.clipboard;
    if (!clipboard) throw new Error('Clipboard access is unavailable. Use Ctrl+V to paste into the editor.');
    let html = '', text = '';
    const images: File[] = [];
    if (clipboard.read) {
        const items = await clipboard.read();
        for (const item of items) {
            for (const type of item.types) {
                if (type !== 'text/html' && type !== 'text/plain' && !type.startsWith('image/')) continue;
                const blob = await item.getType(type);
                if (type === 'text/html') html ||= await blob.text();
                else if (type === 'text/plain') text ||= await blob.text();
                else images.push(new File([blob], 'clipboard-image', { type }));
            }
        }
    } else if (clipboard.readText) text = await clipboard.readText();
    else throw new Error('Clipboard access is unavailable. Use Ctrl+V to paste into the editor.');
    const visibleText = html ? htmlText(html) : text;
    if (!text && html) text = visibleText;
    // Copy Image can include an HTML image and a plain URL representation.
    const imageOnly = !visibleText.trim() && (!!images.length || /<(?:img|svg)\b/i.test(html));
    return { html, text, images, hasText: !imageOnly && !!(text || visibleText) };
}

/** Strip explicit pasted colors, preserving non-color styles and image/SVG content. */
export function filterPastedColors(html: string, mode: PasteMode): string {
    if (mode === 'original' || mode === 'text') return html;
    const foreground = mode !== 'no-background';
    const background = mode !== 'no-foreground';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    for (const element of Array.from(doc.querySelectorAll<HTMLElement>('*'))) {
        if (element.closest('svg')) continue;
        if (foreground) {
            ['color', 'text', 'link', 'vlink', 'alink'].forEach(attribute => element.removeAttribute(attribute));
        }
        if (background) element.removeAttribute('bgcolor');
        const style = element.style;
        for (const property of Array.from(style)) {
            if ((foreground && ['color', '-webkit-text-fill-color', 'text-decoration-color', 'text-shadow'].includes(property))
                || (background && ['background', 'background-color'].includes(property))) style.removeProperty(property);
        }
        // Theme utility classes can otherwise reintroduce colors after paste.
        for (const name of Array.from(element.classList)) {
            if ((foreground && /^text-(?:primary|secondary|color|white|black|\w+-\d+)(?:-|$)/.test(name))
                || (background && /^bg-(?:primary|secondary|white|black|\w+-\d+)(?:-|$)/.test(name))) element.classList.remove(name);
        }
        if (!style.cssText) element.removeAttribute('style');
        if (!element.classList.length) element.removeAttribute('class');
        if (background && element.tagName === 'MARK') {
            const span = doc.createElement('span');
            for (const attribute of Array.from(element.attributes)) span.setAttribute(attribute.name, attribute.value);
            span.append(...Array.from(element.childNodes));
            element.replaceWith(span);
        }
    }
    return doc.body.innerHTML;
}

export function pasteClipboardContent(editor: Editor, content: ClipboardContent, mode: PasteMode): void {
    if (editor.isDestroyed || !editor.isEditable || (mode !== 'original' && !content.hasText)) return;
    const event = editor.view.dom.ownerDocument.createEvent('Event');
    event.initEvent('paste', true, true);
    if (mode === 'text') { editor.view.pasteText(content.text || htmlText(content.html), event as ClipboardEvent); return; }
    // Dispatch through the same capture listener/ProseMirror pipeline used by Ctrl+V.
    const html = filterPastedColors(content.html, mode);
    // Explicit HTML paste bypasses ProseMirror's Shift-key plain-text preference.
    if (mode !== 'original') {
        if (html) editor.view.pasteHTML(html, event as ClipboardEvent);
        else editor.view.pasteText(content.text, event as ClipboardEvent);
        return;
    }
    const images = mode === 'original' ? content.images : [];
    Object.defineProperty(event, 'clipboardData', { value: {
        getData: (type: string) => type === 'text/html' ? html : type === 'text/plain' || type === 'Text' ? content.text : '',
        files: images,
        items: images.map(file => ({ kind: 'file', type: file.type, getAsFile: () => file })),
        types: [html && 'text/html', content.text && 'text/plain', images.length && 'Files'].filter(Boolean),
    } });
    if (html || content.text || images.length) editor.view.dom.dispatchEvent(event);
}
